import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { useState, useEffect, useCallback, useMemo } from "react";

import {
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
} from "../../components/ui/table";
import { LoadingSpinner } from "../../components/ui";
import Button from "../../components/ui/button/Button";
import { getErrorMessage } from "../../utils/axios.utils";
import { PaymentService, Payment } from "../../services/payment.service";
import { ExamRegistration, ExamSession } from "../../types/types";
import { ExamSessionService } from "../../services/exam-session.service";
import { ExamRegistrationService } from "../../services/exam-reg.service";

const SchoolExamPayments = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [examRegistrations, setExamRegistrations] = useState<
    ExamRegistration[]
  >([]);
  const [currentExamSession, setCurrentExamSession] =
    useState<ExamSession | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "mobile_money">(
    "mobile_money"
  );
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [completedPayments, setCompletedPayments] = useState<Payment[]>([]);

  const fetchExamRegistrations = useCallback(async () => {
    try {
      setLoading(true);
      const {
        data: { data },
      } = await ExamRegistrationService.getOwnSchoolRegistrations(1, 30);

      setExamRegistrations(data);
    } catch (error) {
      console.log(getErrorMessage(error));
      toast.error("Failed to fetch exam registrations");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompletedPayments = useCallback(async () => {
    try {
      const response = await PaymentService.getAllPayments(1, 100); // Get all payments (increased limit)
      const completed = response.data.filter(
        (payment: Payment) => payment.payment_status === "completed"
      );
      setCompletedPayments(completed);
    } catch (error) {
      console.log(getErrorMessage(error));
      toast.error("Failed to fetch payment history");
    }
  }, []);

  useEffect(() => {
    fetchExamRegistrations();
    fetchCompletedPayments();

    // Fetch current exam session
    const fetchCurrentExamSession = async () => {
      try {
        const data = await ExamSessionService.getCurrentExamSessions();
        setCurrentExamSession(data);
      } catch (error) {
        console.log(getErrorMessage(error));
        toast.error("Failed to fetch current exam session");
      }
    };

    fetchCurrentExamSession();
  }, [fetchExamRegistrations, fetchCompletedPayments]);

  const handleMakePayment = () => {
    setShowConfirmModal(true);
  };

  const confirmPayment = async () => {
    try {
      setSubmitting(true);

      // Get pending registrations to submit for payment
      const pendingRegistrations = examRegistrations.filter(
        (reg) => reg.payment_status === "pending"
      );

      if (pendingRegistrations.length === 0) {
        toast.error("No pending payments found");
        return;
      }

      // Check if discount is applied
      const isDiscountApplied =
        currentExamSession &&
        new Date() <= new Date(currentExamSession.registration_deadline);

      // Prepare payment details for each registration
      const paymentDetails = pendingRegistrations.map((reg) => {
        const amount = Math.round(Number(reg.total_Fee)); // amount = amount due
        const discountApplied = isDiscountApplied;
        // Use the discount percentage stored at registration time, fallback to 0.16
        const discountPercentage = reg.discount_percentage_used ?? 0.16;
        const discount = discountApplied ? Math.round(amount * discountPercentage) : 0;
        const amount_due = Math.round(amount - discount); // amount_due = payable
        const momo_fee =
          paymentMethod === "mobile_money" ? Math.round(amount_due * 0.015) : 0;
        return {
          exam_registration_id: reg.id,
          amount,
          amount_due,
          momo_fee,
          discount: discountApplied,
        };
      });

      // Create payment payload
      const paymentPayload = {
        payments: paymentDetails,
        exam_registration_ids: paymentDetails.map(
          (p) => p.exam_registration_id
        ),
        payment_method:
          paymentMethod === "mobile_money" ? "mobile_money" : "bank_transfer",
        transaction_id: `TXN-${Date.now()}`,
      };

      // Submit payment to backend
      await PaymentService.createPayment(paymentPayload);

      toast.success("Payment submitted successfully! Status is now pending.");

      if (paymentMethod === "mobile_money") {
        const ussdCode = `*126*4*303323*${totalWithMomoFees}#`;
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        if (isMobile) {
          const link = document.createElement("a");
          link.href = `tel:${ussdCode}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          toast.success(
            `On your mobile device, dial: ${ussdCode} to complete the payment.`,
            { duration: 10000, icon: "📱" }
          );
        }
      }

      setShowConfirmModal(false);

      // Refresh the data to show updated payment statuses
      fetchExamRegistrations();
      fetchCompletedPayments();
    } catch (error) {
      console.log(getErrorMessage(error));
      toast.error("Failed to submit payment");
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate totals for the table
  const { totalAmountDue, totalDiscount, totalPayable, totalWithMomoFees } =
    useMemo(() => {
      let totalAmountDue = 0;
      let totalDiscount = 0;
      let totalPayable = 0;
      let totalWithMomoFees = 0;

      // Calculate total amount due
      examRegistrations.forEach((reg) => {
        if (reg.payment_status === "pending") {
          totalAmountDue += Math.round(Number(reg.total_Fee) || 0);
        }
      });

      // Check if current date is before or equal to registration deadline for discount
      const isEligibleForDiscount =
        currentExamSession &&
        new Date() <= new Date(currentExamSession.registration_deadline);

      // Apply discount if eligible - use stored discount from registrations
      if (isEligibleForDiscount) {
        examRegistrations.forEach((reg) => {
          if (reg.payment_status === "pending") {
            // Use the discount percentage stored at registration time, fallback to 0.16
            const discountPercentage = reg.discount_percentage_used ?? 0.16;
            totalDiscount += Math.round(Number(reg.total_Fee) * discountPercentage);
          }
        });
      }

      // Calculate total payable after discount
      totalPayable = Math.round(totalAmountDue - totalDiscount);

      // Apply MOMO fees if selected payment method is MOMO
      if (paymentMethod === "mobile_money") {
        totalWithMomoFees = Math.round(totalPayable * 1.015); // 1.5% MOMO fee
      } else {
        totalWithMomoFees = totalPayable; // No fee for cash
      }

      // Ensure all values are numbers
      const result = {
        totalAmountDue: Math.round(Number(totalAmountDue) || 0),
        totalDiscount: Math.round(Number(totalDiscount) || 0),
        totalPayable: Math.round(Number(totalPayable) || 0),
        totalWithMomoFees: Math.round(Number(totalWithMomoFees) || 0),
      };

      return result;
    }, [examRegistrations, currentExamSession, paymentMethod]);

  // Determine discount status
  const isEligibleForDiscount =
    currentExamSession &&
    new Date() <= new Date(currentExamSession.registration_deadline);
  const discountReason = isEligibleForDiscount
    ? "Discount applied: Registration is within the deadline."
    : "No discount: Registration deadline has passed.";

  // Group completed payments by exam session
  const groupedCompletedPayments = useMemo(() => {
    const grouped: Record<string, Payment[]> = {};
    completedPayments.forEach((payment) => {
      if (payment.exam_registration) {
        const sessionName = payment.exam_registration.exam_session.name;
        if (!grouped[sessionName]) {
          grouped[sessionName] = [];
        }
        grouped[sessionName].push(payment);
      }
    });
    return grouped;
  }, [completedPayments]);

  return (
    <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-4 py-4 min-h-screen">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          School Exam Payments
        </h1>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Pending payments are shown below. Completed payments are in the
          history section.
        </div>
      </div>

      {/* Persistent Notification Box for Discount Status */}
      <div
        className={`mb-6 p-4 rounded-lg border ${isEligibleForDiscount
          ? "bg-green-50 border-green-200 text-green-800 dark:bg-green-900 dark:border-green-700 dark:text-green-200"
          : "bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900 dark:border-yellow-700 dark:text-yellow-200"
          }`}
      >
        <div className="flex items-center">
          <div className="flex-shrink-0">
            {isEligibleForDiscount ? (
              <svg
                className="h-5 w-5 text-green-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg
                className="h-5 w-5 text-yellow-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium">{discountReason}</p>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
        {loading && <LoadingSpinner size="xl" />}
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 dark:bg-gray-700">
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white"
              >
                S/N
              </TableCell>
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white"
              >
                Exam Type
              </TableCell>
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white"
              >
                Rate per Candidate
              </TableCell>
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white"
              >
                No. Registered
              </TableCell>
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white"
              >
                Amount Due
              </TableCell>
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white"
              >
                Discount
              </TableCell>
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white"
              >
                Payable
              </TableCell>
              <TableCell
                isHeader
                className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white"
              >
                Payment Status
              </TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {examRegistrations.filter((reg) => reg.payment_status === "pending")
              .length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-8 text-gray-500 dark:text-gray-300"
                >
                  No pending payments found for this school.
                </TableCell>
              </TableRow>
            ) : (
              examRegistrations
                .filter((reg) => reg.payment_status === "pending")
                .map((examRegistration, index) => (
                  <motion.tr
                    key={examRegistration.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                      {index + 1}
                    </TableCell>
                    <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                      {examRegistration.exam_type.name}
                    </TableCell>
                    <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                      {examRegistration.exam_type.registration_fee}
                    </TableCell>
                    <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                      {examRegistration.total_students}
                    </TableCell>
                    <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                      {Math.round(Number(examRegistration.total_Fee))}
                    </TableCell>
                    <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                      {/* Discount will be calculated based on session deadline and stored percentage */}
                      {currentExamSession &&
                        new Date() <=
                        new Date(currentExamSession.registration_deadline)
                        ? (
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {Math.round(Number(examRegistration.total_Fee) * (examRegistration.discount_percentage_used ?? 0.16))}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              ({((examRegistration.discount_percentage_used ?? 0.16) * 100).toFixed(2)}%)
                            </span>
                          </div>
                        )
                        : (
                          <div className="flex flex-col">
                            <span className="font-medium">0</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">(0%)</span>
                          </div>
                        )}
                    </TableCell>
                    <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                      {currentExamSession &&
                        new Date() <=
                        new Date(currentExamSession.registration_deadline)
                        ? Math.round(Number(examRegistration.total_Fee) * (1 - (examRegistration.discount_percentage_used ?? 0.16)))
                        : Math.round(Number(examRegistration.total_Fee))}
                    </TableCell>
                    <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                      {examRegistration.payment_status.charAt(0).toUpperCase() +
                        examRegistration.payment_status.slice(1)}
                    </TableCell>
                  </motion.tr>
                ))
            )}
            {!examRegistrations.every(
              (reg) => reg.payment_status === "completed"
            ) ? (
              <>
                {/* Totals row */}
                <TableRow className="bg-gray-100 dark:bg-gray-700 font-bold">
                  <TableCell
                    className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white"
                    colSpan={4}
                  >
                    Total
                  </TableCell>
                  <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                    {totalAmountDue}
                  </TableCell>
                  <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                    {totalDiscount}
                  </TableCell>
                  <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                    {totalPayable}
                  </TableCell>
                </TableRow>
                {/* MOMO Fees row */}
                <TableRow className="bg-gray-50 dark:bg-gray-800 font-bold">
                  <TableCell
                    className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white"
                    colSpan={6}
                  >
                    {paymentMethod === "mobile_money"
                      ? "MOMO Service Fees (1.5%)"
                      : "Total Amount"}
                  </TableCell>
                  <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                    {paymentMethod === "mobile_money"
                      ? totalWithMomoFees - totalPayable
                      : totalPayable}
                  </TableCell>
                </TableRow>
                {/* Final Total row */}
                <TableRow className="bg-blue-50 dark:bg-blue-900 font-bold">
                  <TableCell
                    className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white"
                    colSpan={6}
                  >
                    Final Total
                  </TableCell>
                  <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                    {totalWithMomoFees} FCFA
                  </TableCell>
                </TableRow>
              </>
            ) : null}
          </TableBody>
        </Table>
      </div>

      {/* Payment History Section */}
      {Object.keys(groupedCompletedPayments).length > 0 && (
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Payment History
          </h2>
          {Object.entries(groupedCompletedPayments).map(
            ([sessionName, payments]) => (
              <div key={sessionName} className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  {sessionName}
                </h3>
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50 dark:bg-gray-700">
                        <TableCell
                          isHeader
                          className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white"
                        >
                          Exam Type
                        </TableCell>
                        <TableCell
                          isHeader
                          className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white"
                        >
                          Amount
                        </TableCell>
                        <TableCell
                          isHeader
                          className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white"
                        >
                          Transaction ID
                        </TableCell>
                        <TableCell
                          isHeader
                          className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white"
                        >
                          Payment Date
                        </TableCell>
                        <TableCell
                          isHeader
                          className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white"
                        >
                          Status
                        </TableCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.map((payment) => (
                        <motion.tr
                          key={payment.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                            {payment.exam_registration?.exam_type.name}
                          </TableCell>
                          <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                            {new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: "XAF",
                            }).format(payment.amount)}
                          </TableCell>
                          <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                            {payment.transaction_id}
                          </TableCell>
                          <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                            {new Date(payment.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="px-4 sm:px-6 py-4 text-gray-900 dark:text-white">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
                              Completed
                            </span>
                          </TableCell>
                        </motion.tr>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* Payment Method Selection */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
          Select Payment Method
        </h3>
        <div className="flex gap-4">
          <label className="inline-flex items-center bg-gray-100 dark:bg-gray-700 rounded px-3 py-2 cursor-pointer transition-colors">
            <input
              type="radio"
              name="paymentMethod"
              value="cash"
              checked={paymentMethod === "cash"}
              onChange={() => setPaymentMethod("cash")}
              className="form-radio text-green-600 focus:ring-green-500 dark:focus:ring-green-400"
            />
            <span className="ml-2 text-gray-900 dark:text-white">Cash</span>
          </label>
          <label className="inline-flex items-center bg-gray-100 dark:bg-gray-700 rounded px-3 py-2 cursor-pointer transition-colors">
            <input
              type="radio"
              name="paymentMethod"
              value="mobile_money"
              checked={paymentMethod === "mobile_money"}
              onChange={() => setPaymentMethod("mobile_money")}
              className="form-radio text-yellow-600 focus:ring-yellow-500 dark:focus:ring-yellow-400"
            />
            <span className="ml-2 text-gray-900 dark:text-white">
              Mobile Money (MOMO)
            </span>
          </label>
        </div>
      </div>

      {/* Payment Button */}
      <div className="mt-6 flex justify-end">
        <Button
          variant="success"
          size="md"
          onClick={handleMakePayment}
          disabled={
            examRegistrations.length === 0 ||
            submitting ||
            !examRegistrations.some((reg) => reg.payment_status === "pending")
          }
        >
          {submitting ? "Processing..." : "Make Payment"}
        </Button>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
          }}
        >
          <div className="bg-white dark:bg-gray-900 rounded-lg p-6 w-full max-w-md shadow-2xl border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              Confirm Payment
            </h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              You are about to submit a payment request. Please review the
              details below:
            </p>
            <div className="mb-4">
              <div className="flex justify-between text-gray-900 dark:text-white">
                <span>Amount Due:</span>
                <span>{totalAmountDue}</span>
              </div>
              <div className="flex justify-between text-gray-900 dark:text-white">
                <span>Discount:</span>
                <span>-{totalDiscount}</span>
              </div>
              <div className="flex justify-between text-gray-900 dark:text-white">
                <span>Total Payable:</span>
                <span>{totalPayable}</span>
              </div>
              {paymentMethod === "mobile_money" && (
                <>
                  <div className="flex justify-between text-gray-900 dark:text-white">
                    <span>MOMO Service Fees (1.5%):</span>
                    <span>{totalWithMomoFees - totalPayable}</span>
                  </div>
                  <div className="flex justify-between font-bold text-gray-900 dark:text-white">
                    <span>Final Total:</span>
                    <span>{totalWithMomoFees} FCFA</span>
                  </div>
                </>
              )}
            </div>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              {paymentMethod === "mobile_money" ? (
                <>
                  Dial <strong>*126*4*303323*{totalWithMomoFees}#</strong> to
                  complete the transaction.
                </>
              ) : (
                "Please make payment at the TRU Center for confirmation."
              )}
            </p>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              After payment, the status will be marked as pending and will be
              reviewed by the admin.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowConfirmModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="success"
                onClick={confirmPayment}
                disabled={submitting}
              >
                {submitting ? "Processing..." : "Confirm Payment"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolExamPayments;
